import React from 'react';
import {
  Package,
  Sparkles,
  AlertTriangle,
  Clock,
  RefreshCw,
  AlertCircle,
  CreditCard,
} from 'lucide-react';
import { useEntitlements } from './useEntitlements';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatTranslation } from '../../utils/translations';
import { formatDateTime } from '../../utils/dates';

export const PackagePanel: React.FC = () => {
  const { entitlements, isLoading, isError, refetch } = useEntitlements();
  const { t, language } = useLanguage();

  if (isLoading) {
    return (
      <div role="status" className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs animate-pulse">
        <div className="flex items-center gap-3 text-slate-400">
          <RefreshCw className="w-5 h-5 animate-spin text-emerald-600" />
          <span className="text-xs">{t('packages.loading')}</span>
        </div>
      </div>
    );
  }

  if (isError || !entitlements) {
    return (
      <div role="alert" className="bg-white dark:bg-slate-900 border border-rose-200 dark:border-rose-900/50 rounded-2xl p-6 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3 text-rose-600 dark:text-rose-400">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <p className="text-xs font-medium">{t('packages.unavailable')}</p>
          </div>
          <button
            onClick={() => refetch()}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-xs font-medium text-slate-700 dark:text-slate-200 transition-colors self-start sm:self-auto"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            <span>{t('packages.retry')}</span>
          </button>
        </div>
      </div>
    );
  }

  const {
    package_code,
    active_count,
    effective_limit,
    complimentary_grant,
    pending_package_code,
    grace_deadline,
    packages,
  } = entitlements;

  const getPackageName = (code?: string | null) => {
    if (!code) return '';
    if (code === 'PREMIUM') return t('packages.tierPremium');
    if (code === 'SPECIAL') return t('packages.tierSpecial');
    if (code === 'PRO') return t('packages.tierPro');
    if (code === 'PLUS') return t('packages.tierPlus');
    return t('packages.tierFree');
  };

  const usagePct = effective_limit > 0
    ? Math.min(100, Math.round((active_count / effective_limit) * 100))
    : 0;

  const formattedDeadline = grace_deadline ? formatDateTime(grace_deadline, language) : '';

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-xs space-y-5">
      {/* Header and Current Tier Info */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-slate-100 dark:border-slate-800/80 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
            <Package className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              {t('packages.title')}
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {t('packages.currentPackage')}:{' '}
              <span className="font-bold text-slate-800 dark:text-slate-200">
                {getPackageName(package_code)}
              </span>
            </p>
          </div>
        </div>

        {/* Complimentary Grant & Payments Disabled Badge */}
        {complimentary_grant && (
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-950/50 border border-emerald-200 dark:border-emerald-800/60 text-emerald-700 dark:text-emerald-300 text-xs font-medium self-start sm:self-auto">
            <Sparkles className="w-3.5 h-3.5 text-emerald-500" />
            <span>{t('packages.complimentary')}</span>
          </div>
        )}
      </div>

      {/* Usage Meter */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-slate-600 dark:text-slate-300 font-medium">
            {formatTranslation(t('packages.activeUsage'), {
              active: active_count,
              limit: effective_limit,
            })}
          </span>
          <span className="text-slate-400 font-mono text-[11px] dir-ltr">
            {usagePct}%
          </span>
        </div>
        <div className="w-full h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 rounded-full ${
              usagePct >= 100
                ? 'bg-rose-500'
                : usagePct >= 80
                ? 'bg-amber-500'
                : 'bg-emerald-500'
            }`}
            style={{ width: `${usagePct}%` }}
          />
        </div>
      </div>

      {/* Planned Package Tier Cards Grid */}
      {packages && packages.length > 0 && (
        <div className="space-y-2.5 pt-1">
          <h3 className="text-xs font-medium text-slate-500 dark:text-slate-400">
            {t('packages.plannedPricing')}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {packages.map((pkg) => {
              const isCurrent = pkg.code === package_code;
              const numericPrice = parseFloat(pkg.monthly_price);
              const priceStr = numericPrice === 0 ? '$0' : `$${pkg.monthly_price}`;
              const monthlyPrice = formatTranslation(t('packages.priceMonthly'), {
                price: priceStr,
              });

              return (
                <div
                  key={pkg.code}
                  className={`p-3 rounded-xl border text-xs transition-all ${
                    isCurrent
                      ? 'border-emerald-500/80 bg-emerald-50/40 dark:bg-emerald-950/20 shadow-xs'
                      : 'border-slate-200/80 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/40'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-slate-800 dark:text-slate-200">
                      {getPackageName(pkg.code)}
                    </span>
                    {isCurrent && (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/60 text-emerald-800 dark:text-emerald-200">
                        {t('packages.currentPackage')}
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400">
                    {formatTranslation(t('packages.watchesAllowance'), {
                      limit: pkg.active_watch_limit,
                    })}
                  </p>
                  <p className="mt-2 text-xs font-bold text-slate-900 dark:text-slate-100">
                    {monthlyPrice}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Complimentary and Payments Coming Soon Notice */}
      <div className="flex items-start gap-2.5 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200/60 dark:border-slate-800 text-xs text-slate-600 dark:text-slate-400">
        <CreditCard className="w-4 h-4 shrink-0 mt-0.5 text-slate-400 dark:text-slate-500" />
        <div className="space-y-0.5">
          <p className="font-medium text-slate-700 dark:text-slate-300">
            {t('packages.paymentsComingSoon')}
          </p>
          <p className="text-[11px] leading-relaxed">
            {t('packages.complimentaryDesc')}
          </p>
        </div>
      </div>

      {/* Scheduled Downgrade Warning Banner */}
      {pending_package_code && (
        <div role="alert" className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 text-amber-800 dark:text-amber-200 text-xs space-y-2">
          <div className="flex items-center gap-2 font-semibold">
            <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 shrink-0" />
            <span>
              {t('packages.pendingDowngradeTitle')} ({getPackageName(pending_package_code)})
            </span>
          </div>
          {formattedDeadline && (
            <div className="flex items-center gap-1.5 text-[11px] text-amber-700 dark:text-amber-300">
              <Clock className="w-3.5 h-3.5" />
              <span>
                {formatTranslation(t('packages.graceDeadline'), {
                  deadline: formattedDeadline,
                })}
              </span>
            </div>
          )}
          <p className="text-[11px] leading-relaxed">
            {formatTranslation(t('packages.graceWarning'), {
              target: getPackageName(pending_package_code),
            })}
          </p>
        </div>
      )}
    </div>
  );
};
